import React from 'react';
import {
  TextField,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogContent,
} from '@material-ui/core';
import { Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SearchIcon from '@mui/icons-material/Search';
import AuthUtil from '../../../redux/AuthUtil';
import { DISCOUNT_GROUP } from '../../global/Server/endpoints';
import { PLAN_BADGE_COLORS } from './constants';
import {
  LOCATION_DISCOUNT_TIERS,
  locationTierSummary,
  tierLabel,
} from './wizardConfig';

/**
 * Manage Company > Discount Groups.
 *
 * One container per group: the companies that share the multi-location
 * discount, what their active count earns, and how far the next tier is.
 * Membership here is the ONLY thing that decides the discount — Linked
 * Locations in the company wizard are access, not price. Every change
 * re-prices the upcoming unpaid invoice of each company it touches
 * (DiscountGroupService); the server returns the fresh list each time.
 *
 * Creating a group, adding a company, removing one and deleting a group all
 * happen in dialogs. On a phone (≤ 600px) the two with a search list open
 * full screen and every dialog stacks its buttons full width.
 */

const NARROW_QUERY = '(max-width: 600px)';

const C = {
  navy: '#0f1847',
  navyCard: '#1a2666',
  royal: '#1a3a8f',
  gold: '#D4AF37',
  goldDark: '#B8960C',
  ink: '#0A1640',
  muted: '#5B6781',
  faint: '#8a9cc5',
  line: '#E5EAF4',
  page: '#f4f6fb',
  green: '#047857',
  greenBg: '#ecfdf5',
  grey: '#6B7280',
  greyBg: '#f3f4f6',
  red: '#dc2626',
  redBg: '#fee2e2',
  orange: '#b45309',
  orangeBg: '#fef3c7',
};

const TONES = {
  green: [C.green, C.greenBg],
  grey: [C.grey, C.greyBg],
  orange: [C.orange, C.orangeBg],
  red: [C.red, C.redBg],
};

const card = {
  background: '#fff',
  border: `1px solid ${C.line}`,
  borderRadius: 16,
  boxShadow: '0 2px 10px rgba(15,24,71,0.06)',
  overflow: 'hidden',
};

const iconButton = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: C.faint,
  padding: 6,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
};

const baseButton = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: 'none',
  borderRadius: 10,
  padding: '10px 16px',
  fontWeight: 800,
  fontSize: 13.5,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const BUTTON_KINDS = {
  gold: {
    color: C.navy,
    background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
  },
  danger: { color: '#fff', background: C.red },
  plain: { color: C.muted, background: C.greyBg },
};

const hint = { fontSize: 12.5, color: C.faint, lineHeight: 1.5 };

const tiersAscending = () =>
  LOCATION_DISCOUNT_TIERS.slice().sort(
    (a, b) => (a.minLocations || 0) - (b.minLocations || 0)
  );

// "2–9" / "10+" for the legend.
function tierRange(tier, index, all) {
  const next = all[index + 1];
  return next
    ? `${tier.minLocations}–${next.minLocations - 1}`
    : `${tier.minLocations}+`;
}

function initials(name) {
  const words = String(name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const letters =
    words.length > 1 ? words[0][0] + words[1][0] : words[0] || '?';
  return letters.slice(0, 2).toUpperCase();
}

function Pill({ tone, children }) {
  const [color, background] = TONES[tone] || TONES.grey;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 9px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        color,
        background,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  if (status === 'active') return <Pill tone="green">Active</Pill>;
  if (status === 'closed') return <Pill tone="red">Closed</Pill>;
  return <Pill tone="orange">On hold</Pill>;
}

function PlanBadge({ planName }) {
  if (!planName) return null;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 9px',
        borderRadius: 999,
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        background: PLAN_BADGE_COLORS[planName] || C.grey,
      }}
    >
      {planName}
    </span>
  );
}

function searchMatches(company, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  return (
    (company.name || '').toLowerCase().includes(q) ||
    String(company.companyCode || '')
      .toLowerCase()
      .includes(q)
  );
}

class DiscountGroupsManager extends React.Component {
  state = {
    loading: true,
    saving: false,
    error: '',
    groups: [],
    eligible: [],
    narrow: false,
    // { type: 'create' } | { type: 'add' | 'delete', groupId }
    // | { type: 'remove', groupId, companyId } — or null
    dialog: null,
    newName: '',
    newIds: [],
    newSearch: '',
    addSearch: '',
    renaming: null,
    renameValue: '',
  };

  componentDidMount() {
    this.load();
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.narrowQuery = window.matchMedia(NARROW_QUERY);
      this.syncNarrow();
      if (this.narrowQuery.addEventListener) {
        this.narrowQuery.addEventListener('change', this.syncNarrow);
      } else {
        this.narrowQuery.addListener(this.syncNarrow);
      }
    }
  }

  componentWillUnmount() {
    if (!this.narrowQuery) return;
    if (this.narrowQuery.removeEventListener) {
      this.narrowQuery.removeEventListener('change', this.syncNarrow);
    } else {
      this.narrowQuery.removeListener(this.syncNarrow);
    }
  }

  syncNarrow = () => {
    const narrow = !!(this.narrowQuery && this.narrowQuery.matches);
    if (narrow !== this.state.narrow) this.setState({ narrow });
  };

  // Every call returns the whole list, so the screen always redraws from
  // what the server now holds. `extra` lands in the same render, so a
  // dialog closes together with the change it made.
  applyResponse = (res, extra = {}) => {
    const data = (res && res.data) || {};
    if (data.success === false || !Array.isArray(data.groups)) {
      this.setState({
        loading: false,
        saving: false,
        error: data.message || 'Something went wrong — please try again.',
      });
      return;
    }
    this.setState({
      loading: false,
      saving: false,
      error: '',
      groups: data.groups,
      eligible: data.eligibleCompanies || [],
      ...extra,
    });
  };

  load = async () => {
    this.setState({ loading: true });
    try {
      this.applyResponse(await AuthUtil.getRequest(DISCOUNT_GROUP.LIST));
    } catch (e) {
      this.setState({
        loading: false,
        error: 'Could not load discount groups.',
      });
    }
  };

  send = async (endpoint, body, onSuccess) => {
    this.setState({ saving: true, error: '' });
    try {
      this.applyResponse(await AuthUtil.postRequest(endpoint, body), onSuccess);
    } catch (e) {
      this.setState({ saving: false, error: 'The change could not be saved.' });
    }
  };

  openDialog = (dialog) =>
    this.setState({
      dialog,
      error: '',
      addSearch: '',
      ...(dialog.type === 'create'
        ? { newName: '', newIds: [], newSearch: '' }
        : {}),
    });

  closeDialog = () => {
    if (this.state.saving) return;
    this.setState({ dialog: null, error: '' });
  };

  createGroup = () =>
    this.send(
      DISCOUNT_GROUP.CREATE,
      { name: this.state.newName, companyIds: this.state.newIds },
      { dialog: null }
    );

  renameGroup = (groupId) =>
    this.send(
      DISCOUNT_GROUP.RENAME,
      { groupId, name: this.state.renameValue },
      { renaming: null, renameValue: '' }
    );

  // The Add dialog stays open, so several locations can go in one sitting.
  addCompany = (groupId, companyId) =>
    this.send(
      DISCOUNT_GROUP.ADD_COMPANY,
      { groupId, companyId },
      { addSearch: '' }
    );

  removeCompany = (groupId, companyId) =>
    this.send(
      DISCOUNT_GROUP.REMOVE_COMPANY,
      { groupId, companyId },
      { dialog: null }
    );

  deleteGroup = (groupId) =>
    this.send(DISCOUNT_GROUP.DELETE, { groupId }, { dialog: null });

  groupById = (groupId) =>
    this.state.groups.find((g) => g.groupId === groupId) || null;

  // ---- shared bits -------------------------------------------------------

  button(kind, label, onClick, { disabled, icon } = {}) {
    const off = disabled || this.state.saving;
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={off}
        style={{
          ...baseButton,
          ...BUTTON_KINDS[kind],
          opacity: off ? 0.5 : 1,
          cursor: off ? 'default' : 'pointer',
          width: this.state.narrow ? '100%' : undefined,
        }}
      >
        {this.state.saving && kind !== 'plain' ? (
          <CircularProgress size={14} style={{ color: 'inherit' }} />
        ) : (
          icon
        )}
        {label}
      </button>
    );
  }

  renderSearchField(value, onChange) {
    return (
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        autoFocus
        placeholder="Search Global companies by name or code"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: C.faint, fontSize: 18 }} />
            </InputAdornment>
          ),
        }}
      />
    );
  }

  // Search results — nothing is listed until something is typed; the
  // search finds a location, it doesn't offer suggestions up front.
  renderCompanyResults({ search, exclude, onPick }) {
    const query = (search || '').trim();
    if (!query) {
      return (
        <div style={{ ...hint, padding: '12px 4px' }}>
          Type a location name or code to find it.
        </div>
      );
    }
    const options = this.state.eligible
      .filter((c) => !exclude.includes(c.companyId))
      .filter((c) => searchMatches(c, query))
      .slice(0, 8);
    if (!options.length) {
      return (
        <div style={{ ...hint, padding: '12px 4px' }}>
          No Global location matches “{query}”.
        </div>
      );
    }
    return options.map((c) => {
      const taken = !!c.groupId;
      return (
        <button
          key={c.companyId}
          type="button"
          disabled={taken || this.state.saving}
          onClick={() => onPick(c.companyId)}
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            width: '100%',
            textAlign: 'left',
            border: 'none',
            borderBottom: '1px solid #f0f2f8',
            padding: '12px 6px',
            background: 'transparent',
            cursor: taken ? 'default' : 'pointer',
            opacity: taken ? 0.55 : 1,
            fontSize: 14,
            color: C.ink,
          }}
        >
          <span style={{ fontWeight: 700, minWidth: 0 }}>
            {c.name}
            {c.companyCode ? (
              <span style={{ color: C.faint, fontWeight: 600 }}>
                {' '}
                · {c.companyCode}
              </span>
            ) : null}
          </span>
          <PlanBadge planName={c.planName} />
          <span style={{ flex: 1 }} />
          {taken ? (
            <span style={{ fontSize: 12, color: C.faint }}>
              In {c.groupName}
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: C.royal, fontWeight: 800 }}>
              + Add
            </span>
          )}
        </button>
      );
    });
  }

  // One dialog frame for all four. Search dialogs go full screen on a
  // phone; confirmations stay a card with a 16px gutter.
  renderDialogFrame({ title, subtitle, fullScreenOnNarrow, body, actions }) {
    const { narrow, error, saving } = this.state;
    const fullScreen = narrow && fullScreenOnNarrow;
    return (
      <Dialog
        open
        onClose={this.closeDialog}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: fullScreen
            ? { background: '#fff' }
            : {
                borderRadius: 16,
                margin: 16,
                width: 'calc(100% - 32px)',
                maxWidth: fullScreenOnNarrow ? 520 : 440,
              },
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '18px 16px 10px 20px',
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 800,
                color: C.ink,
                wordBreak: 'break-word',
              }}
            >
              {title}
            </div>
            {subtitle ? (
              <div style={{ ...hint, marginTop: 3 }}>{subtitle}</div>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close"
            style={iconButton}
            disabled={saving}
            onClick={this.closeDialog}
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>
        <DialogContent style={{ padding: '14px 20px' }}>
          {error ? (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                color: C.red,
                background: C.redBg,
              }}
            >
              {error}
            </div>
          ) : null}
          {body}
        </DialogContent>
        <div
          style={{
            display: 'flex',
            flexDirection: narrow ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            gap: 8,
            padding: '12px 20px 18px',
            borderTop: `1px solid ${C.line}`,
          }}
        >
          {actions}
        </div>
      </Dialog>
    );
  }

  renderCreateDialog() {
    const picked = this.state.newIds
      .map((id) => this.state.eligible.find((c) => c.companyId === id))
      .filter(Boolean);
    return this.renderDialogFrame({
      title: 'New discount group',
      subtitle:
        'Name it after the customer, then add the locations they own. You can add or remove companies later.',
      fullScreenOnNarrow: true,
      body: (
        <>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            label="Group name"
            value={this.state.newName}
            onChange={(e) => this.setState({ newName: e.target.value })}
          />
          {picked.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 12,
              }}
            >
              {picked.map((c) => (
                <span
                  key={c.companyId}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    padding: '4px 4px 4px 10px',
                    borderRadius: 999,
                    background: C.navy,
                    color: '#fff',
                    fontSize: 12.5,
                    fontWeight: 700,
                    maxWidth: '100%',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${c.name}`}
                    style={{ ...iconButton, color: '#fff', padding: 3 }}
                    onClick={() =>
                      this.setState((s) => ({
                        newIds: s.newIds.filter((id) => id !== c.companyId),
                      }))
                    }
                  >
                    <CloseIcon style={{ fontSize: 15 }} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            {this.renderSearchField(this.state.newSearch, (v) =>
              this.setState({ newSearch: v })
            )}
            <div style={{ marginTop: 4 }}>
              {this.renderCompanyResults({
                search: this.state.newSearch,
                exclude: this.state.newIds,
                onPick: (id) =>
                  this.setState((s) => ({ newIds: [...s.newIds, id] })),
              })}
            </div>
          </div>
        </>
      ),
      actions: (
        <>
          {this.button('plain', 'Cancel', this.closeDialog)}
          {this.button('gold', 'Create group', this.createGroup, {
            disabled: !this.state.newName.trim(),
            icon: <CheckIcon style={{ fontSize: 18 }} />,
          })}
        </>
      ),
    });
  }

  renderAddDialog(group) {
    const count = group.members.length;
    return this.renderDialogFrame({
      title: `Add to ${group.name}`,
      subtitle: `${count} ${count === 1 ? 'company' : 'companies'} · ${
        group.activeLocations
      } active. Adding a location re-prices the group's next unpaid invoices.`,
      fullScreenOnNarrow: true,
      body: (
        <>
          {this.renderSearchField(this.state.addSearch, (v) =>
            this.setState({ addSearch: v })
          )}
          <div style={{ marginTop: 4 }}>
            {this.renderCompanyResults({
              search: this.state.addSearch,
              exclude: group.members.map((m) => m.companyId),
              onPick: (id) => this.addCompany(group.groupId, id),
            })}
          </div>
        </>
      ),
      actions: this.button('gold', 'Done', this.closeDialog),
    });
  }

  renderRemoveDialog(group, member) {
    const after = Math.max(group.activeLocations - (member.counted ? 1 : 0), 0);
    const tierAfter = locationTierSummary(after).current;
    return this.renderDialogFrame({
      title: `Remove ${member.name}?`,
      body: (
        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
          <b style={{ color: C.ink }}>{member.name}</b> leaves{' '}
          <b style={{ color: C.ink }}>{group.name}</b> and is billed as a single
          location. The group drops to{' '}
          <b style={{ color: C.ink }}>
            {after} active {after === 1 ? 'location' : 'locations'}
          </b>
          {tierAfter
            ? ` and earns ${tierAfter}.`
            : ' — no multi-location discount at that size.'}{' '}
          Their next unpaid invoices are re-priced.
        </div>
      ),
      actions: (
        <>
          {this.button('plain', 'Keep', this.closeDialog)}
          {this.button('danger', 'Remove', () =>
            this.removeCompany(group.groupId, member.companyId)
          )}
        </>
      ),
    });
  }

  renderDeleteDialog(group) {
    const count = group.members.length;
    return this.renderDialogFrame({
      title: `Delete ${group.name}?`,
      body: (
        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6 }}>
          {count ? (
            <>
              Its{' '}
              <b style={{ color: C.ink }}>
                {count} {count === 1 ? 'company' : 'companies'}
              </b>{' '}
              stop sharing the multi-location discount and are billed as single
              locations. Their next unpaid invoices are re-priced.
            </>
          ) : (
            'The group is empty, so no invoice changes.'
          )}{' '}
          The companies themselves aren&apos;t changed.
        </div>
      ),
      actions: (
        <>
          {this.button('plain', 'Cancel', this.closeDialog)}
          {this.button('danger', 'Delete group', () =>
            this.deleteGroup(group.groupId)
          )}
        </>
      ),
    });
  }

  renderDialog() {
    const { dialog } = this.state;
    if (!dialog) return null;
    if (dialog.type === 'create') return this.renderCreateDialog();
    const group = this.groupById(dialog.groupId);
    if (!group) return null;
    if (dialog.type === 'add') return this.renderAddDialog(group);
    if (dialog.type === 'delete') return this.renderDeleteDialog(group);
    if (dialog.type === 'remove') {
      const member = group.members.find(
        (m) => m.companyId === dialog.companyId
      );
      return member ? this.renderRemoveDialog(group, member) : null;
    }
    return null;
  }

  // ---- page --------------------------------------------------------------

  renderIntro() {
    const { narrow } = this.state;
    const tiers = tiersAscending();
    return (
      <div
        style={{
          background: `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`,
          borderRadius: 16,
          padding: narrow ? '16px' : '18px 22px',
          color: '#fff',
          display: 'flex',
          gap: narrow ? 12 : 18,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 18,
          boxShadow: '0 6px 18px rgba(15,24,71,0.18)',
        }}
      >
        {!narrow && (
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: 'rgba(212,175,55,0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <GroupAddIcon style={{ color: C.gold, fontSize: 28 }} />
          </div>
        )}
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>Discount Groups</div>
          <div
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.78)',
              marginTop: 3,
              lineHeight: 1.5,
            }}
          >
            Companies in the same group share the multi-location discount. Only
            active and trial locations count — a location on hold or closed
            stays in its group but gets no discount, and the rest are re-priced
            on what is left.
          </div>
          <div
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}
          >
            {tiers.map((t, i) => (
              <span
                key={t.minLocations}
                style={{
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                {tierRange(t, i, tiers)} active · {tierLabel(t)}
              </span>
            ))}
          </div>
        </div>
        <div style={{ flex: narrow ? '1 1 100%' : '0 0 auto' }}>
          {this.button(
            'gold',
            'New group',
            () => this.openDialog({ type: 'create' }),
            { icon: <AddIcon style={{ fontSize: 18 }} /> }
          )}
        </div>
      </div>
    );
  }

  // How far the group's active count is along the tier ladder.
  renderLadder(active, tier) {
    const tiers = tiersAscending();
    const top = Math.max(...tiers.map((t) => t.minLocations || 0), 1);
    const fill = Math.min(active / top, 1) * 100;
    return (
      <div style={{ marginTop: 14 }}>
        <div
          style={{
            position: 'relative',
            height: 8,
            borderRadius: 999,
            background: '#e8ecf6',
          }}
        >
          <div
            style={{
              width: `${fill}%`,
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${C.royal}, ${C.gold})`,
              transition: 'width .3s',
            }}
          />
          {tiers.map((t) => (
            <Tooltip
              key={t.minLocations}
              title={`${t.minLocations} active · ${tierLabel(t)}`}
            >
              <div
                style={{
                  position: 'absolute',
                  left: `calc(${(t.minLocations / top) * 100}% - 2px)`,
                  top: -3,
                  width: 3,
                  height: 14,
                  borderRadius: 2,
                  background: active >= t.minLocations ? C.gold : '#c5cfe8',
                }}
              />
            </Tooltip>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: 7,
            fontSize: 12,
            color: C.faint,
          }}
        >
          <span>
            {active} active {active === 1 ? 'location' : 'locations'}
          </span>
          <span>{tier.next || 'Top tier reached'}</span>
        </div>
      </div>
    );
  }

  renderMember(group, m) {
    const { narrow } = this.state;
    let discount;
    if (m.discountPercent > 0) {
      discount = <Pill tone="green">−{m.discountPercent}% multi-location</Pill>;
    } else if (!m.counted) {
      discount = (
        <Pill tone="grey">
          {m.companyType === 'alpha' ? 'Alpha · not counted' : 'Not counted'}
        </Pill>
      );
    } else {
      discount = <Pill tone="grey">No discount at this size</Pill>;
    }
    return (
      <div
        key={m.companyId}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: narrow ? 10 : 12,
          padding: narrow ? '12px 12px 12px 14px' : '12px 18px',
          borderTop: '1px solid #f0f2f8',
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: C.navy,
            color: C.gold,
            fontWeight: 800,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {initials(m.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              color: C.ink,
              fontSize: 14,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {m.name}
            {m.companyCode ? (
              <span style={{ color: C.faint, fontWeight: 600 }}>
                {' '}
                · {m.companyCode}
              </span>
            ) : null}
          </div>
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}
          >
            <PlanBadge planName={m.planName} />
            <StatusPill status={m.status} />
            {/* On a phone the discount joins the badge line instead of
                squeezing the name. */}
            {narrow && discount}
          </div>
        </div>
        {!narrow && discount}
        <Tooltip title="Remove from group">
          <button
            type="button"
            aria-label={`Remove ${m.name} from ${group.name}`}
            style={iconButton}
            onClick={() =>
              this.openDialog({
                type: 'remove',
                groupId: group.groupId,
                companyId: m.companyId,
              })
            }
          >
            <CloseIcon style={{ fontSize: 18 }} />
          </button>
        </Tooltip>
      </div>
    );
  }

  renderGroup(group) {
    const { narrow } = this.state;
    const tier = locationTierSummary(group.activeLocations);
    const renaming = this.state.renaming === group.groupId;
    const count = group.members.length;
    const pad = narrow ? 14 : 18;
    return (
      <div key={group.groupId} style={card}>
        <div
          style={{
            padding: `16px ${pad}px`,
            background: 'linear-gradient(180deg, #f7f9ff, #fff)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {renaming ? (
              <>
                <TextField
                  size="small"
                  variant="outlined"
                  autoFocus
                  value={this.state.renameValue}
                  onChange={(e) =>
                    this.setState({ renameValue: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') this.renameGroup(group.groupId);
                    if (e.key === 'Escape') this.setState({ renaming: null });
                  }}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Tooltip title="Save name">
                  <button
                    type="button"
                    aria-label="Save name"
                    style={{ ...iconButton, color: C.green }}
                    disabled={!this.state.renameValue.trim()}
                    onClick={() => this.renameGroup(group.groupId)}
                  >
                    <CheckIcon style={{ fontSize: 20 }} />
                  </button>
                </Tooltip>
                <Tooltip title="Cancel">
                  <button
                    type="button"
                    aria-label="Cancel rename"
                    style={iconButton}
                    onClick={() => this.setState({ renaming: null })}
                  >
                    <CloseIcon style={{ fontSize: 20 }} />
                  </button>
                </Tooltip>
              </>
            ) : (
              <>
                <div
                  style={{
                    fontSize: 16.5,
                    fontWeight: 800,
                    color: C.ink,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {group.name}
                </div>
                <Tooltip title="Rename group">
                  <button
                    type="button"
                    aria-label={`Rename ${group.name}`}
                    style={iconButton}
                    onClick={() =>
                      this.setState({
                        renaming: group.groupId,
                        renameValue: group.name,
                      })
                    }
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                  </button>
                </Tooltip>
                <span style={{ flex: 1 }} />
                <Tooltip title="Delete group">
                  <button
                    type="button"
                    aria-label={`Delete ${group.name}`}
                    style={iconButton}
                    onClick={() =>
                      this.openDialog({
                        type: 'delete',
                        groupId: group.groupId,
                      })
                    }
                  >
                    <DeleteOutlineIcon style={{ fontSize: 20 }} />
                  </button>
                </Tooltip>
              </>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginTop: 8,
            }}
          >
            <span style={{ fontSize: 13, color: C.muted }}>
              {count} {count === 1 ? 'company' : 'companies'} ·{' '}
              {group.activeLocations} active
            </span>
            <Pill tone={tier.current ? 'green' : 'grey'}>
              {tier.current || 'No multi-location discount yet'}
            </Pill>
          </div>
          {this.renderLadder(group.activeLocations, tier)}
        </div>
        <div>
          {group.members.map((m) => this.renderMember(group, m))}
          {!count && (
            <div
              style={{
                ...hint,
                padding: `14px ${pad}px`,
                borderTop: '1px solid #f0f2f8',
              }}
            >
              No companies yet — add the locations this customer owns.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() =>
            this.openDialog({ type: 'add', groupId: group.groupId })
          }
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            width: `calc(100% - ${pad * 2}px)`,
            margin: `12px ${pad}px 16px`,
            padding: '11px',
            border: '1.5px dashed #c5cfe8',
            borderRadius: 12,
            background: '#fafbfe',
            color: C.royal,
            fontWeight: 800,
            fontSize: 13.5,
            cursor: 'pointer',
          }}
        >
          <AddIcon style={{ fontSize: 18 }} /> Add company
        </button>
      </div>
    );
  }

  renderEmpty() {
    return (
      <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}>
        <GroupAddIcon style={{ fontSize: 44, color: '#c5cfe8' }} />
        <div style={{ fontWeight: 800, color: C.ink, marginTop: 8 }}>
          No discount groups yet
        </div>
        <div style={{ ...hint, maxWidth: 420, margin: '6px auto 0' }}>
          Create a group for each customer who runs several locations. Their
          active locations then share the multi-location discount.
        </div>
      </div>
    );
  }

  render() {
    const { loading, error, groups, saving, dialog, narrow } = this.state;
    return (
      <div
        style={{
          background: C.page,
          padding: narrow ? '12px 12px 20px' : '20px 20px 28px',
          // Fill the viewport under the toolbar so the page tint runs to the
          // bottom instead of stopping under the last card.
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {this.renderIntro()}
        {/* Page-level status; a dialog shows its own errors instead. */}
        {!dialog && (error || saving) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 14,
              padding: '10px 14px',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              color: error ? C.red : C.muted,
              background: error ? C.redBg : '#fff',
              border: `1px solid ${error ? '#fecaca' : C.line}`,
            }}
          >
            {saving && !error ? <CircularProgress size={14} /> : null}
            {error || 'Saving and re-pricing the affected invoices…'}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <CircularProgress size={28} />
          </div>
        ) : groups.length === 0 ? (
          this.renderEmpty()
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fill, minmax(min(460px, 100%), 1fr))',
              gap: narrow ? 14 : 18,
              alignItems: 'start',
            }}
          >
            {groups.map((g) => this.renderGroup(g))}
          </div>
        )}
        {this.renderDialog()}
      </div>
    );
  }
}

export default DiscountGroupsManager;
